<template>
  <svg :width="width" :height="height">
    <g :transform="graphTransform">      
      <g class="line" >
        <path
           fill="none"
           :stroke="lineColor"
           stroke-linejoin="round"
           stroke-linecap="round"
           stroke-width="1.5"
           :d="path" />
      </g>
      <g class="bars" >
        <g v-for="(bar, i) in data" :key="bar.x" class="bar hoverable" >
          <popover
             :content="yFormatter(bar.y1)"
             :delay="100"
             :hideDelay="100" >
            <rect
               :x="x(bar.x)"
               :y="y1(bar.y1) + graphHeight * lineRatio"
               :width="Math.max(x.bandwidth(), 0)"
               :height="Math.max(graphHeight * barRatio - y1(bar.y1), 0)"
               :fill="barColor" />
          </popover>
          <g class="line-marker" >
            <circle
               :key="bar.x"
               :cx="x(bar.x) + x.bandwidth() / 2"
               :cy="y2(bar.y2)"
               :r="markerRadius"
               :stroke="lineColor"
               fill="white" />
            <ellipsis
               :x="x(bar.x) + x.bandwidth() / 2"
               :y="y2(bar.y2)"
               :dy="markerRadius / 2 - 8"
               text-anchor="middle"
               :width="markerRadius * 2 - 8"
               :alwaysPopover="false"
               :text="y2Formatter(bar.y2)" />
          </g>
        </g>
      </g>
      <g class="legend" >
        <line
           stroke="silver"
           stroke-width="2"
           x1="0"
           :x2="graphWidth"
           :y1="graphHeight"
           :y2="graphHeight" />
        <g v-for="bar in data" class="label" :transform="labelTransform(bar)" >
          <ellipsis
             :key="bar.x"
             :x="x(bar.x) + x.bandwidth() / 2"
             :y="graphHeight"
             text-anchor="middle"
             :width="marginBottom"
             :text="bar.x" />
        </g>
      </g>
    </g>
    <g class="legend" >
      <rect
         :fill="barColor"
         :x="marginLeft"
         :y="10"
         :width="40"
         :height="10" />
      <ellipsis
         :x="marginLeft + 45"
         :y="10"
         :dy="8"
         :width="Math.min(200, graphWidth / 2 - 50)"
         :alwaysPopover="false"
         :text="barTitle" />
      <line
         :stroke="lineColor"
         :stroke-width="1.5"
         :x1="Math.min(marginLeft + 245, graphWidth / 2 - 50) + 10"
         :x2="Math.min(marginLeft + 245, graphWidth / 2 - 50) + 50"
         :y1="15"
         :y2="15" />
      <ellipsis
         :x="Math.min(marginLeft + 245, graphWidth / 2 - 50) + 55"
         :y="10"
         :dy="8"
         :width="Math.min(200, graphWidth / 2 - 50)"
         :alwaysPopover="false"
         :text="lineTitle" />
    </g>
  </svg>
</template>

<style scoped>
.bar {
    opacity: 0.7;
             }
.bar .line-marker {
    opacity: 0;
}
.bar:hover {
    opacity: 1;
}
.bar:hover .line-marker {
    opacity: 1;
}
.bar.selected {
    stroke: red
}
</style>

<script>
import * as d3 from 'd3'
import * as d3Chromatic from 'd3-scale-chromatic'
import Graph from './Graph.vue'
import margin from '../mixins/margin.js'
import Popover from 'd3vue/components/Popover.vue'
import Ellipsis from './Ellipsis.vue'

export default {
  extends: Graph,
  mixins: [
    margin
  ],
  components: {
    Popover: Popover,
    Ellipsis: Ellipsis
  },
  props: {
    width: {
      type: Number,
      default: 960
    },
    height: {
      type: Number,
      default: 500
    },
    padding: {
      type: Number,
      default: 0.1
    },
    barColor: {
      type: String,
      default: 'steelblue'
    },
    lineColor: {
      type: String,
      default: 'darkred'
    },
    barRatio: {
      type: Number,
      default: 2 / 3
    },
    curve: {
      default: () => {
        return d3.curveMonotoneX
      }
    },
    markerRadius: {
      type: Number,
      default: 28
    },
    y2Formatter: {
      type: Function,
      default: v => v
    },
    barTitle: {
      type: String,
      default: ''
    },
    lineTitle: {
      type: String,
      default: ''
    }
  },
  computed: {
    lineRatio () {
      return 1 - this.barRatio
    },
    x () {
      return d3
        .scaleBand()
        .domain(this.data.map(d => d.x))
        .rangeRound([0, this.graphWidth])
        .padding(this.padding)
    },
    y1 () {
      return d3
        .scaleLinear()
        .domain([0, Math.max(...this.data.map(d => d.y1))])
        .rangeRound([Math.max(this.graphHeight * this.barRatio, this.lineRatio), 0])
        .clamp(true)
    },
    y2 () {
      return d3
        .scaleLinear()
        .domain([0, Math.max(...this.data.map(d => d.y2))])
        .rangeRound([Math.max(this.graphHeight * this.lineRatio, 0), 0])
        .clamp(true)
    },
    line () {
      return d3.line(this.curve).x(d => this.x(d.x) + this.x.bandwidth() / 2).y(d => this.y2(d.y2))
    },
    path () {
      return this.line(this.data)
    }
  },
  methods: {
    labelTransform (bar) {
      return [
        `translate(0, ${this.marginBottom / 2})`,
        `rotate(-65, ${this.x(bar.x) + this.x.bandwidth() / 2}, ${this.graphHeight})`
      ].join(' ')
    }
  }
}
</script>



// WEBPACK FOOTER //
// node_modules/d3vue/components/BarAndLine.vue